import React, {useCallback, useContext, useEffect, useMemo, useReducer, useState} from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import ManageAccountsTwoToneIcon from "@mui/icons-material/ManageAccountsTwoTone";
import MarkEmailReadTwoToneIcon from "@mui/icons-material/MarkEmailReadTwoTone";
import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import clsx from "clsx";
import moment from "moment";
import {useForm} from "react-hook-form";
import {useHistory} from "react-router-dom";
import {AppContext, AuthContext} from "../../../../App";
import {roles} from "../../../../utils/dictionaries/Roles";
import {getCompanyInfo} from "../../../../utils/GenericUtils";
import {setOpenSnackbar} from "../../../../services/AppServices";
import {addRolesToUser, removeRoleFromUser} from "../../../../services/AuthServices";
import {getUsersByCompany, sendResetEmail} from "../../../../services/ReferenteServices";
import {initReferenteState, REFERENTE_INITIAL_STATE, ReferenteReducer} from "../../../../reducers/ReferenteReducer";
import SimpleDialog from "../../../widgets/dialogs/SimpleDialog";
import AutocompleteSelect from "../../../widgets/inputs/AutocompleteSelect";
import SolidTextField from "../../../widgets/inputs/SolidTextField";
import {getMyRolesOptions} from "./CreaUtente";

const formatDate = (value) => {
	const date = moment(value);
	return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "Non disponibile";
};

const getRoleLabel = (role) => roles[role]?.label || role?.replaceAll("ROLE_", "") || role;

const getRoleOptions = () => getMyRolesOptions().map((role) => ({
	value: role,
	label: getRoleLabel(role),
	description: roles[role]?.description || "--",
	color: roles[role]?.color || "var(--atl-blue)",
}));

const getInitials = (value = "") => {
	const [first = "", second = ""] = value.trim().split(/\s+/);
	return `${first.charAt(0)}${second.charAt(0) || first.charAt(1) || ""}`.toUpperCase();
};

const UtentiAzienda = ({classes}) => {
	const history = useHistory();
	const appContext = useContext(AppContext);
	const authContext = useContext(AuthContext);
	const [state, dispatch] = useReducer(ReferenteReducer, REFERENTE_INITIAL_STATE, initReferenteState);
	const {control, errors, handleSubmit, reset} = useForm({mode: "onChange"});
	const [submit, setSubmit] = useState(false);
	const [submitAddRole, setSubmitAddRole] = useState(false);
	const [selected, setSelected] = useState(null);
	const [search, setSearch] = useState("");
	const companyId = getCompanyInfo()?.id;
	const roleOptions = useMemo(() => getRoleOptions(), []);

	const users = useMemo(() => (
		(state.users || [])
			.filter((user) => user.trusted)
			.sort((a, b) => (a.alias || "").localeCompare(b.alias || ""))
	), [state.users]);

	const filteredUsers = useMemo(() => {
		const query = search.trim().toUpperCase();
		if (!query) return users;

		return users.filter((user) => (
			user.alias?.toUpperCase()?.includes(query)
			|| user.username?.toUpperCase()?.includes(query)
			|| user.roles?.some((role) => getRoleLabel(role).toUpperCase().includes(query) || role.toUpperCase().includes(query))
		));
	}, [search, users]);

	const selectUser = useCallback((user) => {
		setSelected(user);
		reset({roles: user.roles || []});
	}, [reset]);

	const onSubmit = useCallback((data) => {
		if (!selected) return;
		setSubmitAddRole(true);
		addRolesToUser(selected, data.roles || [], authContext.dispatch, companyId);
	}, [authContext.dispatch, companyId, selected]);

	const inviaCredenziali = () => {
		if (!selected?.username) return;
		sendResetEmail(selected.username, appContext.dispatch);
		setSubmit(true);
	};

	const removeRole = (user, role) => {
		const nextRoles = (user.roles || []).filter((item) => item !== role);
		setSelected((prevSelected) => prevSelected ? {...prevSelected, roles: nextRoles} : prevSelected);
		reset({roles: nextRoles});
		removeRoleFromUser(user, [role], authContext.dispatch, companyId, dispatch);
	};

	useEffect(() => {
		getUsersByCompany(companyId, dispatch);
	}, [companyId, dispatch]);

	useEffect(() => {
		if (submit && !state.loadingSendEmail && state.errorSendEmail === "") {
			setOpenSnackbar("success", "Email inviata con successo", appContext.dispatch);
			setSubmit(false);
		} else if (submit && !state.loadingSendEmail && state.errorSendEmail.length > 1) {
			setOpenSnackbar("error", state.errorSendEmail, appContext.dispatch);
			setSubmit(false);
		}
	}, [submit, appContext.dispatch, state.errorSendEmail, state.loadingSendEmail]);

	useEffect(() => {
		if (submitAddRole && !authContext.state.loadingAddRole && authContext.state.errorAddRole === "") {
			setSubmitAddRole(false);
			setSelected(null);
			getUsersByCompany(companyId, dispatch);
			setOpenSnackbar("success", "Modifiche apportate con successo", appContext.dispatch);
		} else if (submitAddRole && !authContext.state.loadingAddRole && authContext.state.errorAddRole !== "") {
			setSubmitAddRole(false);
			setOpenSnackbar("error", authContext.state.errorAddRole, appContext.dispatch);
		}
	}, [
		submitAddRole,
		authContext.state.loadingAddRole,
		authContext.state.errorAddRole,
		appContext.dispatch,
		companyId,
		dispatch,
	]);

	return (
		<React.Fragment>
			<section className={classes.panel}>
				<header className={classes.panelHeader}>
					<div>
						<Typography className={classes.panelTitle}>Utenti azienda</Typography>
						<Typography className={classes.panelSubtitle}>
							Gestisci utenti autorizzati, ruoli assegnati e invio credenziali.
						</Typography>
					</div>
					<span className={classes.resultPill}>
						{filteredUsers.length} risultati
					</span>
				</header>

				<div className={classes.userToolbar}>
					<SolidTextField
						fullWidth
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Cerca per alias, username o ruolo"
						size="small"
						value={search}
						variant="outlined"
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<SearchTwoToneIcon fontSize="small"/>
									</InputAdornment>
								),
							},
						}}
					/>
				</div>

				{state.loadingGetUsers && (
					<div className={classes.userGrid}>
						{[0, 1, 2, 3].map((item) => (
							<Skeleton key={item} className={classes.skeletonCard} variant="rounded"/>
						))}
					</div>
				)}

				{!state.loadingGetUsers && state.errorGetUsers !== "" && (
					<Alert severity="error" sx={{borderRadius: 4}}>
						Non e stato possibile recuperare gli utenti dell'azienda.
					</Alert>
				)}

				{!state.loadingGetUsers && state.errorGetUsers === "" && filteredUsers.length === 0 && (
					<Typography className={classes.emptyState}>Nessun utente trovato.</Typography>
				)}

				{!state.loadingGetUsers && state.errorGetUsers === "" && filteredUsers.length > 0 && (
					<div className={classes.userGrid}>
						{filteredUsers.map((user) => {
							const visibleRoles = (user.roles || []).slice(0, 4);
							const extraRoles = Math.max((user.roles || []).length - visibleRoles.length, 0);

							return (
								<button
									key={user.id || user.username}
									className={classes.userCard}
									onClick={() => selectUser(user)}
									type="button"
								>
									<span className={classes.userAvatar}>{getInitials(user.alias || user.username)}</span>
									<span className={classes.userCardBody}>
										<span className={classes.userCardTop}>
											<Typography className={classes.userAlias}>{user.alias || "Utente"}</Typography>
											<span className={clsx(classes.statusPill, user.online && classes.statusPillOnline)}>
												{user.online ? "Online" : "Offline"}
											</span>
										</span>
										<Typography className={classes.userMeta}>{user.username || "Username non disponibile"}</Typography>
										<Typography className={classes.userMeta}>
											Ultimo accesso {formatDate(user.ultimoAccesso)}
										</Typography>
										<span className={classes.rolePreview}>
											{visibleRoles.map((role) => (
												<Chip
													key={role}
													className={classes.roleChip}
													label={getRoleLabel(role)}
													size="small"
													style={{"--role-color": roles[role]?.color || "var(--atl-blue)"}}
												/>
											))}
											{extraRoles > 0 && (
												<Chip
													className={classes.roleChip}
													label={`+${extraRoles}`}
													size="small"
													style={{"--role-color": "var(--atl-blue)"}}
												/>
											)}
										</span>
									</span>
								</button>
							);
						})}
					</div>
				)}
			</section>

			{selected && (
				<SimpleDialog
					title="Modifica utente"
					maxWidth="lg"
					open={Boolean(selected)}
					handleClose={() => {
						setSelected(null);
						setSubmit(false);
						reset({roles: []});
					}}
					showCloseButton
					content={(
						<div className={classes.dialogContent}>
							<div className={classes.factGrid}>
								<div className={classes.fact}>
									<Typography className={classes.factLabel}>Alias</Typography>
									<strong className={classes.factValue}>{selected.alias || "Non disponibile"}</strong>
								</div>
								<div className={classes.fact}>
									<Typography className={classes.factLabel}>Username</Typography>
									<strong className={classes.factValue}>{selected.username || "Non disponibile"}</strong>
								</div>
								<div className={classes.fact}>
									<Typography className={classes.factLabel}>Registrazione</Typography>
									<strong className={classes.factValue}>{formatDate(selected.dataRegistrazione)}</strong>
								</div>
								<div className={classes.fact}>
									<Typography className={classes.factLabel}>Ultimo accesso</Typography>
									<strong className={classes.factValue}>{formatDate(selected.ultimoAccesso)}</strong>
								</div>
								<div className={classes.fact}>
									<Typography className={classes.factLabel}>Scadenza sessione</Typography>
									<strong className={classes.factValue}>{formatDate(selected.scadenzaSessione)}</strong>
								</div>
								<div className={classes.fact}>
									<Typography className={classes.factLabel}>Stato</Typography>
									<strong className={classes.factValue}>{selected.online ? "Online" : "Offline"}</strong>
								</div>
							</div>

							<section className={classes.sectionCard}>
								<Typography className={classes.panelSubtitle}>Ruoli utente</Typography>
								<div className={classes.dialogRoles}>
									{(selected.roles || []).map((role) => (
										<Chip
											key={role}
											className={classes.roleChip}
											label={getRoleLabel(role)}
											onDelete={() => removeRole(selected, role)}
											size="small"
											style={{"--role-color": roles[role]?.color || "var(--atl-blue)"}}
										/>
									))}
								</div>
								<AutocompleteSelect
									className={classes.companySelectField}
									control={control}
									defaultValue={[]}
									error={errors["roles"]}
									helperText={errors["roles"] ? "Seleziona almeno un ruolo" : ""}
									label="Ruoli da assegnare"
									multiple
									name="roles"
									options={roleOptions}
									renderOptionContent={(option) => (
										<div>
											<Typography variant="body2" style={{fontWeight: 850}}>
												{option.label}
											</Typography>
											<Typography variant="caption" color="textSecondary">
												{option.description}
											</Typography>
										</div>
									)}
									renderTags={(value, getTagProps) => value.map((option, index) => (
										<Chip
											{...getTagProps({index})}
											className={classes.roleChip}
											key={option.value}
											label={option.label}
											size="small"
											style={{"--role-color": option.color}}
										/>
									))}
									rules={{required: true}}
									size="small"
								/>
							</section>

						</div>
					)}
					actions={(
						<DialogActions className={classes.dialogActions}>
							<Button
								className={classes.actionButton}
								disableElevation
								onClick={inviaCredenziali}
								size="small"
								startIcon={<MarkEmailReadTwoToneIcon/>}
								variant="outlined"
							>
								Invia credenziali
							</Button>
							<Button
								className={classes.actionButton}
								disableElevation
								onClick={() => history.push(`/gestione/pazienti/${selected.anagraficaAssociata}`)}
								size="small"
								startIcon={<ManageAccountsTwoToneIcon/>}
								variant="outlined"
							>
								Modifica anagrafica
							</Button>
							<Button
								className={classes.actionButton}
								disabled={submitAddRole}
								disableElevation
								onClick={handleSubmit(onSubmit)}
								size="small"
								startIcon={submitAddRole ? <CircularProgress size={16}/> : <SaveTwoToneIcon/>}
								variant="contained"
							>
								Conferma modifiche
							</Button>
						</DialogActions>
					)}
				/>
			)}
		</React.Fragment>
	);
};

export default React.memo(UtentiAzienda);
